'use client';

import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'motion/react';
import { Mesh, Program, Renderer, Triangle } from 'ogl';

/**
 * Hero zemini — ışıklı dalga alanı.
 *
 * Şader React Bits'in "Gradient Waves" bileşeninden (MIT,
 * https://reactbits.dev/backgrounds/gradient-waves). GLSL'e dokunulmadı;
 * değişen şey kabuk — DESIGN.md §1 ve §2'nin istediklerini kaynak sürüm
 * karşılamıyordu:
 *
 *   • **Azaltılmış hareket.** Kaynakta koşulsuz `requestAnimationFrame`
 *     döngüsü var. MotionConfig sonsuz döngüleri kapatmıyor (DESIGN.md §1),
 *     o yüzden burada elle: `prefers-reduced-motion` açıksa tek kare basılıp
 *     duruluyor. Alan yok olmuyor — aynı görüntünün donmuş hâli kalıyor.
 *   • **Piksel bütçesi.** Kaynak `dpr`yi 2'ye sabitliyor. Bu şader ışın
 *     yürütmeli (piksel başına 128'e kadar adım); 2560px'lik ekranda 2x dpr
 *     ~7 Mpx demek ve kare süresi çöküyor. Burada tampon alanı ~2.2 Mpx'e
 *     sınırlanıyor, dpr ona göre hesaplanıyor.
 *   • **Dokunmatik.** İmleç parallax'ı yalnızca `(hover: hover)` ve
 *     `(pointer: fine)` olan cihazlarda açılıyor. Kaynak `pointermove`i
 *     canvas'a bağlıyor; bizde katman `pointer-events: none` (metnin seçimini
 *     engellememesi için), o yüzden dinleyici `window`'a taşındı.
 *   • **Sekme görünürlüğü** ve **viewport dışı** duraklatma kaynakta zaten
 *     vardı, korundu.
 *
 * Renkler bizim paletimizden ve tesadüf değil: `horizonColor`, hero kabının
 * kendi gradient'inin alt ucu (#06292f). Şader uzak mesafede saydama gittiği
 * (`alpha = fog`) için dalgalar ufukta CSS zeminine karışıyor, kesme çizgisi
 * oluşmuyor. Tepe rengi marka yeşili — sayfada başlığın ikinci satırıyla aynı
 * mürekkep, yani zemin markayı tekrar ediyor, yeni bir renk getirmiyor.
 */

export type GradientWavesProps = {
  className?: string;
  /** Uzak mesafe / sis rengi. Hero kabının gradient'iyle aynı olmalı. */
  horizonColor?: string;
  /** Dalga gövdesi. */
  waveColor?: string;
  /** Tepe vurgusu — marka yeşili. */
  crestColor?: string;
  speed?: number;
  amplitude?: number;
  waveScale?: number;
  waveRatio?: number;
  swell?: number;
  turbulence?: number;
  tilt?: number;
  zoom?: number;
  height?: number;
  fogDepth?: number;
  brightness?: number;
  opacity?: number;
  parallaxStrength?: number;
  grainIntensity?: number;
};

/**
 * Işın yürütme adımı = kare başına maliyet. Mobilde düşük, masaüstünde yüksek.
 *
 * React Bits'in "medium" ayarı (70) bizde tırtıklı kenar veriyordu: adım
 * bütçesi biten ışınlar yüzeye varmadan kesiliyor ve silueti basamaklı
 * bırakıyor. Bu en çok ufka yakın, yüzeye teğet geçen ışınlarda görünüyor —
 * yani bizim ışığı topladığımız yerde. Çoğu ışın zaten erken çıkıyor, tavanı
 * yükseltmenin maliyeti yalnızca o teğet bandında.
 */
const STEPS_COARSE = 56;
const STEPS_FINE = 96;

/**
 * Çizim tamponu tavanı. 2.2 Mpx ≈ 1920×1150 — 1440px'lik hero'nun 1.5x'i.
 * Bunun üstünde kare süresi 16 ms'i aşmaya başlıyor (raymarch adım sayısı
 * piksel başına sabit, yani maliyet doğrudan alanla artıyor).
 */
const MAX_BUFFER_PIXELS = 2_200_000;

const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m || !m[1] || !m[2] || !m[3]) return [1, 1, 1];
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
};

const vertex = /* glsl */ `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragment = /* glsl */ `#version 300 es
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
uniform float uSpeed;
uniform float uAmplitude;
uniform float uWaveScale;
uniform float uWaveRatio;
uniform float uSwell;
uniform float uTurbulence;
uniform float uTilt;
uniform float uZoom;
uniform float uHeight;
uniform float uFogDepth;
uniform float uSteps;
uniform float uBrightness;
uniform float uOpacity;
uniform float uGrain;
uniform float uGrainIntensity;
uniform vec2 uMouse;
uniform float uParallax;
uniform bool uEnableMouse;
uniform vec3 uHorizonColor;
uniform vec3 uWaveColor;
uniform vec3 uCrestColor;
out vec4 fragColor;

const float MAX_DIST = 20000.0;

float hash21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float plasma(vec3 r, vec2 freq, vec4 tc) {
  float mx = r.x + tc.x;
  mx += uSwell * sin((r.y + mx) / 20.0 + tc.y);
  float my = r.y - tc.z;
  my += uTurbulence * cos(r.x / 23.0 + tc.w);
  return r.z - (sin(mx * freq.x) * uAmplitude + sin(my * freq.y) * uAmplitude + uHeight);
}

float raymarch(vec3 pos, vec3 dir, vec2 freq, vec4 tc) {
  float dist = 0.0;
  for (int i = 0; i < 128; i++) {
    if (float(i) >= uSteps) break;
    float dscene = plasma(pos + dist * dir, freq, tc);
    if (abs(dscene) < 0.1) break;
    dist += 0.9 * dscene;
    if (!(abs(dist) < MAX_DIST)) return MAX_DIST;
  }
  return dist;
}

void main() {
  float T = iTime * uSpeed;
  vec2 freq = vec2(uWaveScale / 7.0, (uWaveScale * uWaveRatio) / 3.0);
  vec4 tc = vec4(T / 0.130, T / 0.810, T / 0.200, T / 0.710);
  float c, s;
  float vfov = (3.14159 / 2.3) / max(uZoom, 0.05);
  vec3 cam = vec3(0.0, 0.0, 30.0);
  vec2 uv = (gl_FragCoord.xy / iResolution.xy) - 0.5;
  uv.x *= iResolution.x / iResolution.y;
  uv.y *= -1.0;

  vec3 dir = vec3(0.0, 0.0, -1.0);
  float ulen = length(uv);
  float xrot = vfov * ulen;
  c = cos(xrot); s = sin(xrot);
  dir = mat3(1.0, 0.0, 0.0, 0.0, c, -s, 0.0, s, c) * dir;
  vec2 nuv = ulen > 1e-5 ? uv / ulen : vec2(1.0, 0.0);
  c = nuv.x; s = nuv.y;
  dir = mat3(c, -s, 0.0, s, c, 0.0, 0.0, 0.0, 1.0) * dir;
  c = cos(uTilt); s = sin(uTilt);
  dir = mat3(c, 0.0, s, 0.0, 1.0, 0.0, -s, 0.0, c) * dir;

  if (uEnableMouse) {
    float yaw = (uMouse.x - 0.5) * uParallax * 0.4;
    float pitch = (uMouse.y - 0.5) * uParallax * 0.4;
    c = cos(yaw); s = sin(yaw);
    dir = mat3(c, 0.0, s, 0.0, 1.0, 0.0, -s, 0.0, c) * dir;
    c = cos(pitch); s = sin(pitch);
    dir = mat3(1.0, 0.0, 0.0, 0.0, c, -s, 0.0, s, c) * dir;
  }

  float dist = raymarch(cam, dir, freq, tc);
  vec3 pos = cam + dist * dir;

  float t = clamp(uFogDepth / max(dist, 0.001), 0.0, 1.0);
  vec3 body = mix(uWaveColor, uCrestColor, clamp(pos.z * 0.08 + 0.5, 0.0, 1.0));
  vec3 col = mix(uHorizonColor, body, t);
  col *= uBrightness;
  col = clamp(col, 0.0, 1.0);

  float alpha = clamp(t, 0.0, 1.0) * uOpacity;
  if (uGrain > 0.5) {
    float g = hash21(gl_FragCoord.xy + mod(iTime, 64.0) * 11.0);
    alpha += (g - 0.5) * uGrainIntensity;
  }
  alpha = clamp(alpha, 0.0, 1.0);
  fragColor = vec4(col * alpha, alpha);
}
`;

/* Adları tek tek sayılıyor: tsconfig'de `noUncheckedIndexedAccess` açık, indeks
   imzalı bir Record her erişimi `| undefined` yapar ve 20 satır `!` doğurur. */
type UniformName =
  | 'iTime'
  | 'iResolution'
  | 'uSpeed'
  | 'uAmplitude'
  | 'uWaveScale'
  | 'uWaveRatio'
  | 'uSwell'
  | 'uTurbulence'
  | 'uTilt'
  | 'uZoom'
  | 'uHeight'
  | 'uFogDepth'
  | 'uSteps'
  | 'uBrightness'
  | 'uOpacity'
  | 'uGrain'
  | 'uGrainIntensity'
  | 'uMouse'
  | 'uParallax'
  | 'uEnableMouse'
  | 'uHorizonColor'
  | 'uWaveColor'
  | 'uCrestColor';

type Uniforms = { [K in UniformName]: { value: number | boolean | Float32Array } };

type Handle = {
  uniforms: Uniforms;
  start: () => void;
  stop: () => void;
  drawOnce: () => void;
};

export function GradientWaves({
  className,
  horizonColor = '#030018',
  waveColor = '#06292f',
  crestColor = '#12e193',
  speed = 0.16,
  amplitude = 2.6,
  waveScale = 0.72,
  waveRatio = 0.9,
  swell = 35,
  turbulence = 20,
  tilt = 1.11,
  zoom = 1,
  height = 5.5,
  fogDepth = 28,
  brightness = 0.85,
  opacity = 0.38,
  parallaxStrength = 0.25,
  grainIntensity = 0.03,
}: GradientWavesProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<Handle | null>(null);
  const reduce = useReducedMotion();

  /* --- Kurulum. Bir kez; prop değişimi ikinci effect'ten uniform'a yazılır. */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new Renderer({
      webgl: 2,
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      dpr: 1,
    });

    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    const canvas = gl.canvas as HTMLCanvasElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    container.appendChild(canvas);

    /* İnce imleci olmayan cihazda parallax yok: DESIGN.md §2 (hover yalnızca
       hover destekleyen cihazda) ve §1 (vestibüler yük) birlikte. */
    const finePointer =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const coarse = !finePointer || window.innerWidth < 900;

    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new Float32Array([1, 1]) },
        uSpeed: { value: speed },
        uAmplitude: { value: amplitude },
        uWaveScale: { value: waveScale },
        uWaveRatio: { value: waveRatio },
        uSwell: { value: swell },
        uTurbulence: { value: turbulence },
        uTilt: { value: tilt },
        uZoom: { value: zoom },
        uHeight: { value: height },
        uFogDepth: { value: fogDepth },
        uSteps: { value: coarse ? STEPS_COARSE : STEPS_FINE },
        uBrightness: { value: brightness },
        uOpacity: { value: opacity },
        uGrain: { value: grainIntensity > 0 ? 1 : 0 },
        uGrainIntensity: { value: grainIntensity },
        uMouse: { value: new Float32Array([0.5, 0.5]) },
        uParallax: { value: parallaxStrength },
        uEnableMouse: { value: finePointer },
        uHorizonColor: { value: new Float32Array(hexToRgb(horizonColor)) },
        uWaveColor: { value: new Float32Array(hexToRgb(waveColor)) },
        uCrestColor: { value: new Float32Array(hexToRgb(crestColor)) },
      },
    });

    const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });
    const uniforms = program.uniforms as Uniforms;

    const draw = () => renderer.render({ scene: mesh });

    /* Tampon alanını tavana göre ölçekle — bkz. MAX_BUFFER_PIXELS. */
    const setSize = () => {
      const rect = container.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      const raw = Math.min(window.devicePixelRatio || 1, 2);
      const area = w * h * raw * raw;
      renderer.dpr = area > MAX_BUFFER_PIXELS ? raw * Math.sqrt(MAX_BUFFER_PIXELS / area) : raw;
      renderer.setSize(w, h);
      const res = uniforms.iResolution.value as Float32Array;
      res[0] = gl.drawingBufferWidth;
      res[1] = gl.drawingBufferHeight;
      draw();
    };

    const ro = new ResizeObserver(setSize);
    ro.observe(container);
    setSize();

    /* Katman `pointer-events: none` — dinleyici canvas'a değil window'a bağlı,
       konum canvas'ın dikdörtgenine göre normalleniyor. */
    const current: [number, number] = [0.5, 0.5];
    const target: [number, number] = [0.5, 0.5];

    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      target[0] = (e.clientX - rect.left) / rect.width;
      target[1] = 1 - (e.clientY - rect.top) / rect.height;
    };
    const onPointerLeave = () => {
      target[0] = 0.5;
      target[1] = 0.5;
    };
    if (finePointer) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      document.addEventListener('pointerleave', onPointerLeave);
    }

    let raf = 0;
    let inView = true;
    let pageVisible = !document.hidden;
    /* Azaltılmış hareket ilk karede biliniyor olmalı: useReducedMotion ilk
       render'da null döner, bir kare animasyon sızardı. */
    let animate = !(
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    const t0 = performance.now();

    const loop = (t: number) => {
      uniforms.iTime.value = (t - t0) * 0.001;
      current[0] += 0.05 * (target[0] - current[0]);
      current[1] += 0.05 * (target[1] - current[1]);
      const m = uniforms.uMouse.value as Float32Array;
      m[0] = current[0];
      m[1] = current[1];
      draw();
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      if (animate && inView && pageVisible && raf === 0) raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (raf !== 0) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    handleRef.current = {
      uniforms,
      start: () => {
        animate = true;
        start();
      },
      stop: () => {
        animate = false;
        stop();
      },
      drawOnce: draw,
    };

    const io = new IntersectionObserver(
      (entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        if (inView) start();
        else stop();
      },
      { threshold: 0 },
    );
    io.observe(container);

    const onVisibility = () => {
      pageVisible = !document.hidden;
      if (pageVisible) start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);

    start();

    return () => {
      stop();
      handleRef.current = null;
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerleave', onPointerLeave);
      canvas.remove();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
    // Kurulum bir kez çalışır; prop'lar aşağıdaki effect'ten uniform'a yazılır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- Azaltılmış hareket. Alan kaybolmuyor, donuyor. --------------------- */
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle || reduce === null) return;
    if (reduce) {
      handle.stop();
      handle.uniforms.uEnableMouse.value = false;
      handle.drawOnce();
    } else {
      handle.start();
    }
  }, [reduce]);

  /* --- Prop → uniform. Yeniden kurulum yok, tek kare bile kaybedilmiyor. -- */
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    const u = handle.uniforms;
    u.uSpeed.value = speed;
    u.uAmplitude.value = amplitude;
    u.uWaveScale.value = waveScale;
    u.uWaveRatio.value = waveRatio;
    u.uSwell.value = swell;
    u.uTurbulence.value = turbulence;
    u.uTilt.value = tilt;
    u.uZoom.value = zoom;
    u.uHeight.value = height;
    u.uFogDepth.value = fogDepth;
    u.uBrightness.value = brightness;
    u.uOpacity.value = opacity;
    u.uParallax.value = parallaxStrength;
    u.uGrain.value = grainIntensity > 0 ? 1 : 0;
    u.uGrainIntensity.value = grainIntensity;
    (u.uHorizonColor.value as Float32Array).set(hexToRgb(horizonColor));
    (u.uWaveColor.value as Float32Array).set(hexToRgb(waveColor));
    (u.uCrestColor.value as Float32Array).set(hexToRgb(crestColor));
    if (reduce) handle.drawOnce();
  }, [
    horizonColor,
    waveColor,
    crestColor,
    speed,
    amplitude,
    waveScale,
    waveRatio,
    swell,
    turbulence,
    tilt,
    zoom,
    height,
    fogDepth,
    brightness,
    opacity,
    parallaxStrength,
    grainIntensity,
    reduce,
  ]);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
}
